import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Offer } from "@/services/backend/htz/offers/types/offer";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import {
  OfferDocument,
  OfferImmutabilityReason,
  useHtzOfferPdfDownloadMutation,
  useHtzOfferPdfPreviewQuery,
  useHtzOfferSetImmutableMutation,
} from "@/services/backend/htz/offers/api/offer-api";
import { useOfferContext } from "@/routes/processes/[processId]/htz/offers/_components/offer-context";
import { rtkErrIsValidationError } from "@/shared/lib/errors/validation-error";
import { rtkErrIsErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";

export function OfferPdf({ offer }: { offer: Offer }) {
  const { data, isLoading, error } = useHtzOfferPdfPreviewQuery({
    offerId: offer.id,
    document: OfferDocument.ALL,
  });

  const { validationError, setValidationError } = useOfferContext();

  const { user } = useAuth();

  return (
    <DocumentSheet>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Angebot")}
              documentNumber={<DocumentNumberById documentId={offer.id} />}
            />
            <DocumentSheetActionsContent>
              {!offer.immutable && (
                <>
                  <DocumentNotReadyAlert
                    error={validationError}
                    document={t("Angebot")}
                  />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      `Um eine Angebotsnummer zu generieren müssen Sie das Angebot festschreiben. Sobald das Angebot festgeschrieben ist, wird das Wasserzeichen "Vorschau" entfernt. Sie können das Angebot jederzeit herunterladen. Das Wasserzeichen wird erst entfernt, wenn es festgeschrieben wurde.`,
                    )}
                  />
                  <Separator />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: offer.id,
                      reason: OfferImmutabilityReason.SetByUser,
                      setBy: user?.id || "",
                    }}
                    mutation={useHtzOfferSetImmutableMutation}
                    description={t(
                      "Das Angebot wird festgeschrieben und es wird eine Angebotsnummer generiert. Danach kann das Angebot nicht mehr geändert werden.",
                    )}
                    onError={(err) => {
                      if (rtkErrIsErrDocumentNotReady(err)) {
                        setValidationError(err.data);
                      }
                    }}
                  />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.ALL}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.OFFER}
                  text={t("Angebot")}
                />
                <DownloadDocumentButton
                  offerId={offer.id}
                  document={OfferDocument.ORDER}
                  text={t("Auftrag")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  offerId,
  document: documentType,
  text,
}: {
  offerId: string;
  document: OfferDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useHtzOfferPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  const { setValidationError } = useOfferContext();
  if (rtkErrIsValidationError(error) || rtkErrIsErrDocumentNotReady(error)) {
    setValidationError(error.data);
    setOpen(false);
    reset();
  }

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({
      offerId,
      document: documentType,
      download: true,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Angebot herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn ein Angebot heruntergeladen wird, wird es festgeschrieben. Es kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
